import { ButtonV2, Toggle } from '@cimplico/components';
import { ChevronDoubleRightIcon } from '@heroicons/react/20/solid';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentFocusField, selectSchema } from '../store/selectors';
import { useSearchParams } from 'react-router-dom';
import { isField, sortByOrder } from '../store/utils';
import { CWState } from '../store/slice';
import { getNavHeightWithScroll } from '../utils';
import useWindowResize from '../../../../hooks/use-window-resize';
import NonFieldItem from './NonFieldItem';
import FieldItem from './FieldItem';

function TreeNav(): JSX.Element {
    const { width } = useWindowResize();
    const currentFocusField = useSelector(selectCurrentFocusField);

    // 1024px is tailwind lg screen size
    const largeScreen = width <= 1024;
    const smallScreen = width <= 1500;

    const [isCollapsed, setIsCollapsed] = useState(!!largeScreen);
    const [search, setSearch] = useSearchParams();
    const [filter, setFilter] = useState<'all' | 'remaining'>('all');

    const schema = useSelector((state: { schema: CWState }) => selectSchema({ state, filter }));

    const addon = search.get('addon');

    const navRef = useRef<HTMLDivElement>(null);

    const [searchParams] = useSearchParams();
    const hasWorksheetPreview = searchParams.has('worksheetPreview');

    // Function to close comment/query add on side bar
    function handleClose(): void {
        setSearch(
            (s) => {
                s.delete('addon');
                return s;
            },
            { replace: true }
        );
    }

    // Function to collapse/expand tree nav
    function handleCollapse(): void {
        // If tree nav is expanded on a small screen, ensure add on sidebar is collapsed
        if (smallScreen && isCollapsed && addon) {
            handleClose();
        }
        setIsCollapsed(!isCollapsed);
    }

    // Function to collapse navbar on small screen after blur
    const handleBlur = useCallback(
        function handleBlur(): void {
            if (!largeScreen || isCollapsed) {
                return;
            }
            setIsCollapsed(true);
        },
        [isCollapsed, largeScreen]
    );

    // When the window scrolls, recalculate the nav height.
    const handleScroll = useCallback(function handleScroll() {
        if (navRef.current) {
            navRef.current.style.height = `${getNavHeightWithScroll(navRef.current)}px`;
        }
    }, []);

    // collapse if the worksheet side panel preview opens up
    useEffect(() => {
        if (hasWorksheetPreview) {
            setIsCollapsed(true);
        }
    }, [hasWorksheetPreview]);

    // When add on sidebar is expanded on a small screen, ensure tree nav is collapsed
    useEffect(() => {
        if (smallScreen && addon && !isCollapsed) {
            setIsCollapsed(true);
        }
    }, [addon, isCollapsed, smallScreen]);

    // When active field is set, call handleBlur to collapsed tree nav on small screen
    useEffect(() => {
        if (currentFocusField) {
            handleBlur();
        }
    }, [currentFocusField, handleBlur]);

    // Listen for scroll events
    useEffect(() => {
        window.addEventListener('scroll', handleScroll, true);
        return (): void => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [handleScroll]);

    return (
        <div
            ref={navRef}
            style={{ height: getNavHeightWithScroll(navRef.current), scrollbarGutter: 'stable' }}
            className="flex flex-col top-[70px] sticky overflow-x-hidden overflow-y-auto min-w-max overscroll-contain"
        >
            <motion.aside
                initial={false}
                animate={{ width: isCollapsed ? 48 : 300 }}
                transition={{ duration: 0.2 }}
                className="flex flex-col gap-4 relative shrink-0"
                onBlur={handleBlur}
            >
                <div className="flex items-center pl-2 pr-3">
                    <p className="text-gray-500 font-medium overflow-hidden">Navigation</p>
                    <div className="ml-auto">
                        <ButtonV2
                            leftIcon={<ChevronDoubleRightIcon className={`${isCollapsed ? 'rotate-180' : ''} transition-all`} />}
                            size="xs"
                            type="secondary"
                            onClick={handleCollapse}
                        />
                    </div>
                </div>
                <motion.div
                    initial={false}
                    animate={{ opacity: isCollapsed ? 0 : 1, transition: { duration: 0.05, delay: isCollapsed ? 0 : 0.175 } }}
                    className={`overflow-hidden ${isCollapsed ? 'pointer-events-none h-0' : ''}`}
                >
                    <div className="flex items-center gap-2 pl-2 pb-4 text-xs font-medium text-gray-700">
                        <p>Hide completed</p>
                        <Toggle
                            defaultEnabled={filter === 'remaining'}
                            onToggle={(): void => setFilter(filter === 'all' ? 'remaining' : 'all')}
                            type="short"
                        />
                    </div>
                    {sortByOrder(schema.properties).map((value) => {
                        return isField(value) ? (
                            <FieldItem key={value.id} field={value} />
                        ) : (
                            <NonFieldItem key={value.id} section={value} />
                        );
                    })}
                </motion.div>
            </motion.aside>
        </div>
    );
}

export default TreeNav;
